<template>
  <Head :title="__('business.meta.title')">
    <meta
      name="description"
      :content="__('business.meta.description')"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.business')"
    >
    <meta
      property="og:image"
      content="/frontend/assets/favicon/og-image.jpg"
    >
  </Head>

  <section class="business-hero pt-14 md:pt-4">
    <div class="md:container">
      <div class="flex flex-wrap md:items-center">
        <div class="w-full text-center md:w-1/2 md:order-2 md:px-8 md:text-left md:pt-14">
          <div class="container md:px-0">
            <span class="border border-red px-2 text-xl font-bold mx-auto text-red rounded-md inline-block mb-2">
              {{ __('business.tag') }}
            </span>

            <h1 class="text-4xl font-bold mb-2 lg:text-5xl">
              {{ __('business.title') }}
            </h1>

            <p class="lg:text-xl">
              {{ __('business.text') }}
            </p>
          </div>
        </div>

        <div class="w-full relative pr-6 md:w-1/2 md:order-1">
          <img
            src="/frontend/assets/business-mobile.png"
            class="md:absolute md:left-0 md:hidden mr-4"
          >
          <img
            src="/frontend/assets/business-desktop.png"
            class="hidden md:block md:pr-8"
          >
        </div>
      </div>
    </div>
  </section>

  <section class="business-text mb-0 md:mb-12 py-24 bg-white text-lg relative">
    <img
      src="/frontend/assets/scribble.svg"
      class="absolute -top-[48px] left-1/2 transform -translate-x-1/2"
    >

    <div class="container">
      <h2 class="text-4xl font-bold mb-6 text-center md:w-3/4 md:mx-auto lg:text-5xl lg:mb-8">
        {{ __('business.intro.title') }}
      </h2>

      <p
        v-for="(value, index) in $tm('business.intro.parts')"
        :key="index"
      >
        <strong>{{ value.title }}</strong><br>
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="value.text" />
        <img
          v-if="value.image"
          :src="value.image"
        >
      </p>

      <i18n-t
        keypath="business.outro.text"
        scope="global"
      >
        <template #link>
          <a
            :href="__('business.outro.file')"
            target="_blank"
            class="text-red"
          >
            {{ __('business.outro.link') }}
          </a>
        </template>
      </i18n-t>
    </div>

    <img
      src="/frontend/assets/scribble.svg"
      class="hidden md:block absolute -bottom-[48px] left-1/2 transform -translate-x-1/2"
    >
  </section>

  <section class="business-contact py-12 md:pb-24 relative">
    <div class="container">
      <div class="lg:w-3/4 lg:mx-auto relative">
        <img
          src="/frontend/assets/scribble-top-desktop-black.svg"
          class="hidden lg:block lg:absolute lg:-right-[90px] lg:-top-[30px]"
        >

        <div
          v-if="success"
          class="text-center border-b border-gray-300 pb-2"
        >
          <h2 class="text-4xl font-bold lg:text-5xl text-red">
            {{ __('business.form.success.title') }}
          </h2>

          <p class="text-lg">
            {{ __('business.form.success.text') }}
          </p>
        </div>

        <div
          v-if="!success"
          class="text-center border-b border-gray-300 pb-2"
        >
          <h2 class="text-4xl font-bold lg:text-5xl">
            {{ __('business.form.title') }}
          </h2>

          <p class="text-lg">
            {{ __('business.form.text') }}
          </p>
        </div>

        <form
          v-if="!success"
          @submit.prevent="form.post(localeRoute('frontend.contact.store'), {
            onSuccess: () => form.reset(),
            onError: () => {
              form.recaptcha = '';
              if ($refs.recaptcha) {
                $refs.recaptcha.reset();
              }
            },
          })"
        >
          <div class="flex flex-wrap mt-4 lg:w-11/12 lg:mx-auto">
            <div class="w-full md:w-1/2 mb-4 md:pr-2">
              <InputField
                v-model="form.name"
                :form="form"
                name="name"
                type="text"
                :label="__('business.form.name.label')"
                :placeholder="__('business.form.name.placeholder')"
              />
            </div>

            <div class="w-full md:w-1/2 mb-4 md:pl-2">
              <InputField
                v-model="form.email"
                :form="form"
                name="email"
                type="email"
                :label="__('business.form.email.label')"
                :placeholder="__('business.form.email.placeholder')"
              />
            </div>

            <div class="w-full mb-4">
              <InputField
                v-model="form.phone"
                :form="form"
                name="phone"
                type="tel"
                :label="__('business.form.phone.label')"
                :placeholder="__('business.form.phone.placeholder')"
                :is-required="false"
              />
            </div>

            <div class="w-full mb-4">
              <TextareaField
                v-model="form.message"
                :form="form"
                name="message"
                :label="__('business.form.message.label')"
                :placeholder="__('business.form.message.placeholder')"
                rows="8"
              />
            </div>

            <div class="mb-4">
              <VueRecaptcha
                ref="recaptcha"
                :sitekey="recaptchaSiteKey"
                @verify="(response) => form.recaptcha = response"
                @expired="() => form.recaptcha = ''"
              />

              <p
                v-if="form.errors.recaptcha"
                class="text-red text-xs italic"
              >
                {{ __('business.form.recaptcha-invalid') }}
              </p>
            </div>

            <div class="w-full text-center">
              <button
                type="submit"
                class="button-orange shadow-button-black w-auto inline-block !px-16"
              >
                {{ __('business.form.send') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <img
      src="/frontend/assets/scribble-round-desktop.svg"
      class="hidden lg:block absolute -left-[5px] top-[300px]"
    >
  </section>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import { VueRecaptcha } from 'vue-recaptcha';
import InputField from '../../Components/Form/InputField.vue';
import TextareaField from '../../Components/Form/TextareaField.vue';

export default {
  components: {
    VueRecaptcha,
    InputField,
    TextareaField,
  },

  props: {
    recaptchaSiteKey: {
      type: String,
      required: true,
    },

    success: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const form = useForm({
      name: '',
      email: '',
      phone: '',
      message: '',
      recaptcha: '',
    });

    return {form};
  },
};
</script>
